import React from "react";

import { useMutation } from "@apollo/client";
import { Close as CloseIcon, Error as ErrorIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, SubmitHandler } from "react-hook-form";
import { useParams } from "react-router-dom";
import { makeStyles, withStyles } from "tss-react/mui";
import * as yup from "yup";

import waterIntakeCardLogo from "@/assets/images/water-intake-log-icon.png";
import { graphql } from "@/gql";
import { UpdateJournalGoalInput } from "@/gql/graphql";
import useFormWithYup from "@/hooks/useFormWithYup";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { theme } from "@/theme/customTheme";
import { colors } from "@/theme/overrides/colors";
import { muiField, muiFieldError } from "@/utils/hookForm";

export const validationSchema = yup.object().shape({
  waterGoal: yup
    .number()
    .required("Water intake is required")
    .nullable()
    .transform((value) => (isNaN(value) ? null : value))
    .test(
      "has2decimal",
      "Value must not exceed 2 decimal places",
      function (value) {
        if (!value) return true;
        if (Number.isInteger(value)) return true;
        const result = value.toString().split(".")[1]
          ? value.toString().split(".")[1].length <= 2
          : true;
        return result;
      }
    ),
});

type ModalProps = {
  visibility: boolean;
  onClose: () => void;
  currentWaterGoal: string;
};
const useStyles = makeStyles()((theme) => ({
  root: {
    overflowY: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
  modalBody: {
    display: "flex",
    flexDirection: "column",
    margin: theme.spacing(24, 24),
    backgroundColor: colors.white,
    overflowY: "auto",
    width: "470px",
  },
  modalHeader: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    margin: theme.spacing(4, 3, 0, 3),
  },
  modalHeaderText: {
    fontSize: "28px",
    fontWeight: "bold",
  },
  titleText: {
    fontSize: "14px",
    fontWeight: 700,
    color: colors.dark,
    marginBottom: "20px",
  },
  form: {
    margin: theme.spacing(4, 3, 0, 3),
  },
  fields: {
    width: "100%",
  },
  fieldContainer: {
    marginBottom: "20px",
  },
  labelInfo: {
    color: colors.secondary,
    "& .MuiSvgIcon-root": {
      marginRight: "5px",
      marginTop: "4px",
    },
  },
  error: {
    fontSize: theme.typography.pxToRem(12),
  },
  weightGoal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: colors.light,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(4),
  },
  droplet: {
    objectFit: "cover",
    height: "28px",
    width: "28px",
  },
}));

const FieldLabel = withStyles(Typography, {
  root: {
    color: colors.gray,
    fontSize: "14px",
    marginBottom: "12px",
    "& label": {
      color: "#D13F3F",
    },
  },
});

type FormValues = {
  waterGoal: string;
};

const updateClientWaterIntakeGoalMutation = graphql(`
  mutation UpdateClientWaterIntakeGoal($input: UpdateJournalGoalInput!) {
    updateJournalGoal(input: $input) {
      journalGoal {
        id
        targetWaterIntakeMl
      }
    }
  }
`);

const EditWaterGoalModal: React.FC<ModalProps> = ({
  visibility,
  onClose,
  currentWaterGoal,
}) => {
  const { classes } = useStyles();
  const { clientId } = useParams<{ clientId: string }>();
  const [updateWaterIntakeGoal] = useMutation(
    updateClientWaterIntakeGoalMutation
  );
  const snackbar = useGlobalSnackbar();

  const { control, handleSubmit, reset } = useFormWithYup<FormValues>({
    defaultValues: {
      waterGoal: "",
    },
    mode: "onChange",
    reValidateMode: "onBlur",
    validationSchema,
  });

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    const formValues = validationSchema.cast(values);
    try {
      const input: UpdateJournalGoalInput = {
        clientId,
        attributes: {
          targetWaterIntakeMl: formValues?.waterGoal,
        },
      };
      const result = await updateWaterIntakeGoal({ variables: { input } });

      if (result.data) {
        snackbar.open("Water goal updated successfully!");
      }
    } catch {
      snackbar.open("Unable to update water goal. Please try again later.");
    }
    reset();
    onClose();
  };

  const _handleOnClose = () => {
    reset();
    onClose();
  };

  return (
    <>
      <Modal
        open={visibility}
        onClose={_handleOnClose}
        className={classes.root}
      >
        <Box className={classes.modalBody}>
          <Box className={classes.modalHeader}>
            <Typography className={classes.modalHeaderText}>
              Edit Water Goal
            </Typography>
            <IconButton onClick={onClose} size="large">
              <CloseIcon />
            </IconButton>
          </Box>
          <form
            className={classes.form}
            noValidate
            onSubmit={handleSubmit(onSubmit)}
          >
            <Box>
              <div className={classes.weightGoal}>
                <Box display="flex" alignItems="center">
                  <img src={waterIntakeCardLogo} className={classes.droplet} />
                  <Box marginRight={theme.spacing(0.5)} />
                  <Typography variant="body1" fontWeight="bold">
                    Current Goal
                  </Typography>
                </Box>
                <Typography variant="h6" fontWeight="bold">
                  {currentWaterGoal} ml
                </Typography>
              </div>
              <Box className={classes.fieldContainer}>
                <FieldLabel>New Water Goal</FieldLabel>
                <Controller
                  name="waterGoal"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <TextField
                        {...muiField(field)}
                        {...muiFieldError(error)}
                        className={classes.fields}
                        type="number"
                        placeholder="E.g. 2500 ml"
                      />
                    );
                  }}
                />
                <Typography className={classes.labelInfo}>
                  <ErrorIcon fontSize="small" />
                  The goal changes here will reflect on the profile&apos;s
                  current water goal
                </Typography>
              </Box>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                marginBottom={4}
              >
                <Button color="primary" type="submit">
                  Save
                </Button>
              </Box>
            </Box>
          </form>
        </Box>
      </Modal>
    </>
  );
};

export default EditWaterGoalModal;
